// src/components/recheck/RecheckVerdictBar.jsx
//
// The three re-check verdicts: confirm, reject not_present, reject
// imagery_unclear (141's feature_assertions_recheck_chk). A reject needs a note
// (D-5). Every button is disabled unless the cell's lock is held and the
// time strip's gate allows a verdict here, which is only on the item's own
// collect: never on Sentinel-2, never on another epoch (the server's 422
// wrong_epoch is the same rule).
//
// A CORRECTED POSITION (DB 2026-10-25). While the page holds a draft, Confirm
// confirms at that position and says how far it moved the well; the rejects
// are off, since a well that is not there has no corrected position. A draft
// the server would refuse cannot be confirmed: past RECHECK_POSITION_LIMIT_M
// (amber), or still on the item's own point. Between the warning and the
// limit (10 and 50 m, DB 2026-10-26) it is amber and asks the reviewer to
// check it is the same well; Confirm stays enabled.

import { useState, useEffect } from "react";
import { RECHECK_POSITION_LIMIT_M, formatMeters } from "./recheckPosition";
import { FOOTER_BUTTON } from "./footerButton";

export const RECHECK_VERDICTS = Object.freeze([
  { id: "confirm", label: "Confirm", detail: "The well is on this collect", assertion_type: "confirm", reject_reason: null },
  { id: "not_present", label: "Not present", detail: "No well here on this collect", assertion_type: "reject", reject_reason: "not_present" },
  { id: "imagery_unclear", label: "Imagery unclear", detail: "This collect cannot show it", assertion_type: "reject", reject_reason: "imagery_unclear" },
]);

/**
 * @param {object} props
 * @param {{allowed: boolean, message: string|null}} props.gate   from verdictGate()
 * @param {boolean} props.lockHeld
 * @param {boolean} props.busy
 * @param {string} props.resetKey   the item id: a new item clears the note and the picked reject
 * @param {{distance_m: number, tooFar: boolean, advisory?: boolean, unchanged: boolean}|null} [props.draft]
 *        the corrected position's standing, from draftState(); null when there is none
 * @param {(v: {assertion_type: string, reject_reason: string|null, note: string|null}) => void} props.onSubmit
 */
export default function RecheckVerdictBar({ gate, lockHeld, busy, resetKey, draft = null, onSubmit }) {
  const [reject, setReject] = useState(null);
  const [note, setNote] = useState("");
  useEffect(() => { setReject(null); setNote(""); }, [resetKey]);

  const blocked = !lockHeld ? "Take this cell's lock to give verdicts." : (!gate?.allowed ? gate?.message : null);
  const disabled = Boolean(blocked) || busy;
  const offFor = (v) => disabled || (draft != null
    && (v.assertion_type !== "confirm" || draft.tooFar || draft.unchanged));
  const position = draft == null ? null
    : draft.tooFar ? { state: "too_far", text: `${formatMeters(draft.distance_m)} from the item's point: a corrected position must be within ${RECHECK_POSITION_LIMIT_M} m.` }
    : draft.unchanged ? { state: "unchanged", text: "Drag the marker to where the well is, or press Esc to cancel." }
    : draft.advisory ? { state: "advisory", text: `Moved ${formatMeters(draft.distance_m)}: check this is the same well` }
    : { state: "ok", text: "Rejects are off while a corrected position is placed. Esc discards it." };

  const pick = (v) => {
    if (offFor(v)) return;
    if (v.assertion_type === "confirm") { onSubmit({ assertion_type: "confirm", reject_reason: null, note: null }); return; }
    setReject(v);
  };
  const submitReject = () => {
    if (!reject || !note.trim()) return;
    onSubmit({ assertion_type: "reject", reject_reason: reject.reject_reason, note: note.trim() });
  };

  return (
    <div className="flex flex-col gap-2" data-testid="recheck-verdicts">
      <div className="flex flex-wrap gap-2">
        {RECHECK_VERDICTS.map((v) => (
          <button
            key={v.id} type="button" disabled={offFor(v)}
            data-testid={`recheck-verdict-${v.id}`}
            title={v.detail}
            aria-pressed={reject?.id === v.id}
            onClick={() => pick(v)}
            className="rounded border border-bg-border px-3 py-1 text-sm text-fg-primary disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {v.assertion_type === "confirm" && draft ? `Confirm at corrected position (${formatMeters(draft.distance_m)})` : v.label}
          </button>
        ))}
      </div>
      {blocked && <div className="text-xs text-amber-300" data-testid="recheck-verdict-blocked">{blocked}</div>}
      {position && !blocked && (
        <div
          data-testid="recheck-position-status" data-state={position.state}
          className={`text-xs ${position.state === "too_far" || position.state === "advisory" ? "text-amber-300" : "text-fg-secondary"}`}
        >
          {position.text}
        </div>
      )}
      {reject && !disabled && !draft && (
        <div className="flex flex-col gap-1">
          <label className="text-xs text-fg-secondary" htmlFor="recheck-note">
            Note (required for {reject.label.toLowerCase()})
          </label>
          <textarea
            id="recheck-note" data-testid="recheck-note" rows={2} value={note}
            onChange={(e) => setNote(e.target.value)}
            className="rounded border border-bg-border bg-bg-soft px-2 py-1 text-sm text-fg-primary"
          />
          <div className="flex gap-2">
            <button
              type="button" data-testid="recheck-submit-reject" disabled={!note.trim() || busy}
              onClick={submitReject}
              className="rounded border border-bg-border px-3 py-1 text-sm text-fg-primary disabled:opacity-40"
            >
              Submit {reject.label.toLowerCase()}
            </button>
            <button type="button" onClick={() => setReject(null)} className={FOOTER_BUTTON}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
